import { AuthUserPermission } from '@cibo/core'
import { Can, useAuth } from '@cibo/profile'
import { Alert, AlertTitle, CircularProgress, Collapse, Fade, useTheme } from '@mui/material'
import { groupBy, prop } from 'ramda'
import { Trans, useTranslation } from 'react-i18next'
import { useAgreements } from '../queries'
import { AgreementType } from '../types'

type PendingAgreementsAlertProps = {
  onClick(): void
}

export const PendingAgreementsAlert = ({ onClick }: PendingAgreementsAlertProps) => {
  const { t } = useTranslation('impact/components/PendingAgreementsAlert')
  const { userId } = useAuth()
  const pendingAgreements = useAgreements({ status: 'pending', userId })

  const agreementGroups = groupBy(prop('agreementType'), pendingAgreements.data?.items || [])
  const numAgreements = pendingAgreements.data?.numAvailable

  const {
    palette: { mode },
  } = useTheme()

  return (
    <Can useAny={[AuthUserPermission.AGREEMENTS]}>
      <Collapse in={!!numAgreements} sx={{ marginTop: !!numAgreements ? 0 : -3 }}>
        <Fade in={!!numAgreements}>
          <Alert
            onClick={onClick}
            sx={{ '&:hover': { cursor: 'pointer' } }}
            severity="info"
            icon={pendingAgreements.isLoading ? <CircularProgress size={22} /> : undefined}
            variant={mode === 'dark' ? 'filled' : 'standard'}
          >
            <AlertTitle data-testid="alertTitle">
              {Object.keys(agreementGroups).length === 1
                ? t('alertTitle', {
                    count: numAgreements,
                    context: pendingAgreements.data?.items[0].agreementType,
                  })
                : t('groupSummary', {
                    count: numAgreements,
                    val: Object.keys(agreementGroups).map((group: string) =>
                      t('group', {
                        context: group,
                        count: agreementGroups[group as AgreementType].length,
                      })
                    ),
                  })}
            </AlertTitle>

            <Trans i18nKey={`impact/components/PendingAgreementsAlert:cta`} count={numAgreements}>
              You have agreement(s) <strong>click</strong> to review
            </Trans>
          </Alert>
        </Fade>
      </Collapse>
    </Can>
  )
}
